import type { BlobType } from "@pdfking/blob"
import { BlobNative } from "@pdfking/blob"
import { GetImage, GetOffscreenCanvas } from "@pdfking/capability"
import type { ImageArray, ImportImageType } from "@pdfking/compatibility"
import { BlobCollect } from "@pdfking/io"
import type { Pixel } from "@pdfking/math"

import { CreateObjectURL, RevokeObjectURL } from "#compatibility"

const importWithImageBitmap = async (blob: BlobType): Promise<ImageArray> => {
  const bitmap = await BlobNative(blob).then(createImageBitmap)
  const { height, width } = bitmap

  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Failed to get canvas 2d context")

  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const imageData = ctx.getImageData(0, 0, width, height)
  return {
    data: new Uint8Array(imageData.data.buffer),
    height: height as Pixel,
    width: width as Pixel,
  }
}

const importWithImage = async (blob: BlobType): Promise<ImageArray> => {
  const url = await CreateObjectURL(blob)

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      void RevokeObjectURL(url)
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")!
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, img.width, img.height)
      resolve({
        data: new Uint8Array(imageData.data.buffer),
        height: img.height as Pixel,
        width: img.width as Pixel,
      })
    }
    img.onerror = e => {
      void RevokeObjectURL(url)
      reject(e)
    }
    img.src = url
  })
}

const importBlob = (blob: BlobType): Promise<ImageArray> => {
  if (GetOffscreenCanvas()) return importWithImageBitmap(blob)
  if (GetImage()) return importWithImage(blob)
  throw new Error("No image import implementation available")
}

// Collected rather than passed along: a decoder wants one blob it can sniff,
// and whatever came in may be parts, a stream, or a response.
export const ImportImage: ImportImageType = input => BlobCollect(input).then(importBlob)
